import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { getGroqKey, clearGroqKey } from "./History";

const NAV_LINKS = [
  { path: "/", label: "Home" },
  { path: "/scribe", label: "Scribe" },
  { path: "/interview", label: "Interview" },
  { path: "/sop", label: "Prose" },
  { path: "/mirror", label: "Mirror", soon: true },
];

export default function Nav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [darkMode, setDarkMode] = useState(() =>
    localStorage.getItem("lliuq_theme") === "dark"
  );

  useEffect(() => {
    document.body.classList.toggle("dark", darkMode);
    localStorage.setItem("lliuq_theme", darkMode ? "dark" : "light");
  }, [darkMode]);

  const [showKey, setShowKey] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [groqKey, setGroqKey] = useState(() => getGroqKey());

  useEffect(() => {
    const sync = () => setGroqKey(getGroqKey());
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  const handleSave = () => {
    if (newKey.startsWith("gsk_")) {
      import("./History").then(h => {
        h.saveGroqKey(newKey);
        setGroqKey(newKey);
        setIsEditing(false);
        setNewKey("");
        // Reload to update other components that rely on the key
        window.location.reload();
      });
    }
  };

  const handleForget = () => {
    if (window.confirm("Clear all API keys?")) {
      import("./History").then(h => {
        h.clearGroqKey();
        setGroqKey("");
        setIsEditing(true);
        setNewKey("");
      });
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;1,400&family=DM+Mono:wght@400;500&display=swap');

        .lliuq-nav {
          height: 60px;
          background: #1a1714;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 28px;
          position: sticky;
          top: 0;
          z-index: 1000;
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }

        .lliuq-logo {
          font-family: 'Playfair Display', serif;
          font-size: 20px;
          font-weight: 700;
          color: #f5f2ee;
          cursor: pointer;
          letter-spacing: -0.02em;
          display: flex;
          align-items: center;
          gap: 3px;
          text-decoration: none;
          transition: opacity 0.2s;
        }

        .lliuq-logo:hover { opacity: 0.8; }

        .lliuq-logo em {
          font-style: italic;
          color: #c8571a;
        }

        .lliuq-nav-links {
          display: flex;
          align-items: center;
          gap: 6px;
          list-style: none;
        }

        .lliuq-nav-link {
          font-family: 'DM Mono', monospace;
          font-size: 11px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: rgba(245,242,238,0.5);
          cursor: pointer;
          padding: 6px 14px;
          border-radius: 2px;
          transition: all 0.15s;
          border: 1px solid transparent;
          position: relative;
        }

        .lliuq-nav-link:hover {
          color: rgba(245,242,238,0.85);
          background: rgba(255,255,255,0.05);
        }

        .lliuq-nav-link.active {
          color: #f5f2ee;
          background: rgba(255,255,255,0.08);
          border-color: rgba(255,255,255,0.1);
        }

        .lliuq-nav-soon {
          font-size: 8px;
          letter-spacing: 0.1em;
          color: #c8571a;
          margin-left: 5px;
          vertical-align: super;
          text-transform: uppercase;
        }

        .lliuq-nav-link--disabled {
          opacity: 0.35;
          cursor: default;
        }

        .lliuq-nav-link--disabled:hover {
          background: transparent;
          color: rgba(245,242,238,0.5);
        }

        .nav-key-input {
          background: #2d2c2b;
          border: 1px solid rgba(255,255,255,0.1);
          color: #f5f2ee;
          font-family: 'DM Mono', monospace;
          font-size: 10px;
          padding: 4px 8px;
          border-radius: 4px;
          outline: none;
          width: 140px;
          transition: border-color 0.2s;
        }

        .nav-key-input:focus { border-color: #c8571a; }

        body.dark .lliuq-nav { background: #252423; border-color: rgba(255,255,255,0.08); }
        body.dark .lliuq-nav-link { color: rgba(243,242,241,0.5); }
        body.dark .lliuq-nav-link:hover { color: rgba(243,242,241,0.85); background: rgba(255,255,255,0.05); }
        body.dark .lliuq-nav-link.active { color: #f3f2f1; background: rgba(255,255,255,0.08); }

      `}</style>

      <nav className="lliuq-nav">
        <span className="lliuq-logo" onClick={() => navigate("/")}>
          <img src="/lliuq.png" alt="lliuq" style={{ height: 28, width: 28, borderRadius: 5, objectFit: "cover" }} />
          lliuq<em>.</em>
        </span>
        <ul className="lliuq-nav-links">
          {NAV_LINKS.map((l) => (
            <li
              key={l.path}
              className={[
                "lliuq-nav-link",
                (l.path === "/" ? pathname === "/" : pathname === l.path) ? "active" : "",
                l.soon ? "lliuq-nav-link--disabled" : "",
              ].join(" ").trim()}
              onClick={() => !l.soon && navigate(l.path)}
            >
              {l.label}
              {l.soon && <span className="lliuq-nav-soon">soon</span>}
            </li>
          ))}
        </ul>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {isEditing || !groqKey ? (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                className="nav-key-input"
                type="password"
                name="groq_api_key_nav"
                id="groq_api_key_nav"
                placeholder="gsk_..."
                value={newKey}
                onChange={e => setNewKey(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleSave()}
                autoComplete="new-password"
              />
              <button
                onClick={handleSave}
                disabled={!newKey.startsWith("gsk_")}
                style={{
                  background: newKey.startsWith("gsk_") ? "#c8571a" : "rgba(255,255,255,0.1)",
                  border: "none",
                  color: "#fff",
                  fontSize: 10,
                  padding: "4px 10px",
                  borderRadius: 4,
                  cursor: "pointer"
                }}
              >
                Save
              </button>
              {groqKey && <button onClick={() => setIsEditing(false)} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.5)", fontSize: 13, cursor: "pointer" }}>✕</button>}
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {showKey && (
                <div style={{
                  background: "rgba(255,255,255,0.08)",
                  padding: "4px 10px",
                  borderRadius: 4,
                  fontSize: 10,
                  color: "#f5f2ee",
                  fontFamily: "'DM Mono', monospace",
                  border: "1px solid rgba(255,255,255,0.1)"
                }}>
                  {groqKey.slice(0, 8)}...{groqKey.slice(-4)}
                </div>
              )}
              <button
                onClick={() => setShowKey(!showKey)}
                title={showKey ? "Hide key" : "View key"}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, opacity: 0.6, padding: 0 }}
              >
                {showKey ? "🙈" : "👁️"}
              </button>
              <button
                onClick={handleForget}
                title="Permanently remove your API key"
                style={{
                  background: "none",
                  border: "1px solid rgba(224,96,80,0.3)",
                  color: "#e06050",
                  fontSize: 9,
                  padding: "2px 6px",
                  borderRadius: 3,
                  cursor: "pointer",
                  fontFamily: "'DM Mono', monospace",
                  textTransform: "uppercase"
                }}
              >
                Clear
              </button>

              <button
                onClick={() => setIsEditing(true)}
                title="Update your API key"
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "rgba(245,242,238,0.8)",
                  fontSize: 9,
                  padding: "2px 6px",
                  borderRadius: 3,
                  cursor: "pointer",
                  fontFamily: "'DM Mono', monospace",
                  textTransform: "uppercase"
                }}
              >
                Edit
              </button>
            </div>
          )}

          <div
            onClick={() => setDarkMode(d => !d)}
            title="Toggle dark mode"
            style={{ cursor: "pointer", color: "rgba(245,242,238,0.5)", padding: "4px 8px", borderRadius: 4, fontSize: 16, transition: "opacity 0.2s" }}
          >
            {darkMode ? "☀️" : "🌙"}
          </div>
        </div>

      </nav>
    </>
  );
}